import { DisplayFormFieldsConfig, DisplayFormRef } from './types';
import { useForm, Control, FieldValues } from 'react-hook-form';
import { useDisplayForm } from './useDisplay';
import styled, { css } from 'styled-components';
import {
    FormEventHandler,
    ForwardedRef,
    forwardRef,
    ReactNode,
    Ref,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { AudienceAutocomplete } from 'jamespot-user-api';

const StretchContainer = css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const Container = styled.div<{ stretch?: boolean }>`
    ${({ stretch }) => stretch && StretchContainer}
`;

const Form = styled.form<{ stretch?: boolean }>`
    ${({ stretch }) => stretch && StretchContainer}
`;

const FormElement = (
    props: {
        stretch?: boolean;
        onSubmit?: FormEventHandler<HTMLFormElement>;
        children: ReactNode;
        dataCy?: string;
    },
    ref: ForwardedRef<HTMLFormElement | HTMLDivElement>,
) => {
    return props.onSubmit ? (
        <Form
            stretch={props.stretch}
            onSubmit={props.onSubmit}
            ref={ref as Ref<HTMLFormElement>}
            data-cy={props.dataCy}>
            {props.children}
        </Form>
    ) : (
        <Container stretch={props.stretch} ref={ref as Ref<HTMLDivElement>} data-cy={props.dataCy}>
            {props.children}
        </Container>
    );
};
const FormElm = forwardRef(FormElement);

const FieldsetWrapper = styled.div<{ stretch?: boolean }>`
    ${(props) =>
        props.stretch &&
        `
    flex: 1;
`}
`;

type FormFn<TFieldValues extends FieldValues> = Omit<
    ReturnType<typeof useForm<TFieldValues>>,
    'handleSubmit' | 'control' | 'reset'
>;

export type DisplayFormProps<TFieldValues extends FieldValues> = {
    type: string;
    fields: DisplayFormFieldsConfig<TFieldValues>;
    extraInputs?: (control: Control<TFieldValues>) => ReactNode;
    defaultValues?: any;
    onSubmit?: any;
    buttons?: ReactNode;
    readOnly?: boolean;
    dataCy?: string;
    stretch?: boolean;
    children?: ReactNode;
    forceMandatory?: boolean;
    onInit?: (formFn: FormFn<TFieldValues>) => void;
};

/****
 * The jamespot model depend on the platform
 * For example a user can have the size attribute only for a given platform
 * The model specifics of a platform are stored in J.model
 *
 * This component display an array of attributes only if this attribute is activated on the platform
 */
const _DisplayForm = <TFieldValues extends FieldValues>(
    { onInit, ...props }: DisplayFormProps<TFieldValues>,
    ref: ForwardedRef<DisplayFormRef>,
) => {
    const formRef = useRef<HTMLFormElement | HTMLDivElement>(null);

    const [configuration, defaultValues] = useDisplayForm(props.fields, props.type, props.forceMandatory);
    const [isInit, setIsInit] = useState(false);

    const { handleSubmit, control, reset, ...formFn } = useForm({
        defaultValues: {
            ...defaultValues,
            ...props.defaultValues,
        },
        criteriaMode: 'all',
    });

    useEffect(() => {
        if (onInit && !isInit) {
            onInit(formFn);
            setIsInit(true);
        }
    }, [onInit, formFn, isInit]);

    const formatValuesForSubmit = useCallback(
        (values: any) => {
            const toRet: any = {};
            Object.keys(values).forEach((k) => {
                const widget = configuration.find((c) => c.name === k)?.widget;
                switch (widget?.type) {
                    case 'audience':
                        toRet[k] = values[k].map((v: AudienceAutocomplete) => v.uri).join(',');
                        break;
                    case 'taxonomy':
                        toRet[k] = values[k].map((v: any) => v.title).join(',');
                        break;
                    case 'toggle':
                        toRet[k] = values[k] ? '1' : '0';
                        break;
                    default:
                        toRet[k] = values[k];
                        break;
                }
            });
            return toRet;
        },
        [configuration],
    );

    useImperativeHandle(
        ref,
        () => ({
            ...formFn,
            handleSubmit,
            control,
            reset: () => {
                // in case the form is scrollable
                formRef?.current?.scrollTo(0, 0);
                // reset react-hook-form form
                reset();
            },
            getFormattedValues: () => {
                return formatValuesForSubmit(formFn.getValues());
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <FormElm
            dataCy={props.dataCy}
            stretch={props.stretch}
            onSubmit={props.onSubmit ? handleSubmit(props.onSubmit) : undefined}
            ref={formRef}>
            <FieldsetWrapper stretch={props.stretch}>
                {configuration.map(({ Input, ...conf }) => (
                    <Input
                        key={conf.name}
                        control={control}
                        {...conf}
                        dataCy={`${props.dataCy}-${conf.name}`}
                        readOnly={props.readOnly}
                    />
                ))}
                {props.extraInputs && props.extraInputs(control)}
            </FieldsetWrapper>
            {props.buttons}
        </FormElm>
    );
};

export const DisplayForm = forwardRef<DisplayFormRef>(_DisplayForm as any) as <TFieldValues extends FieldValues>(
    props: DisplayFormProps<TFieldValues> & {
        ref?: ForwardedRef<DisplayFormRef>;
    },
) => ReturnType<typeof _DisplayForm>;
